import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    if (this.closest('main[data-template^="product"]') && this.presentation === 'carousel') {
      this.classList.add('about-gallery-with-upcoming');
      this.#upcoming = document.createElement('div');
      this.#upcoming.className = 'about-gallery-upcoming';
      this.append(this.#upcoming);
      this.addEventListener('slideshow:select', this.#updateUpcoming, { signal });
      requestAnimationFrame(() => this.#renderUpcoming(this.slideshow?.current ?? 0));
    }
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #upcoming;

  #updateUpcoming = (event) => {
    if (event.target !== this.slideshow) return;
    this.#renderUpcoming(event.detail.index);
  };

  #renderUpcoming(index) {
    if (!this.#upcoming || !this.isConnected) return;
    const slides = Array.from(this.slideshow?.slides ?? []);
    const buttons = [];
    for (let offset = 1; offset < slides.length && buttons.length < 5; offset++) {
      const next = (index + offset) % slides.length;
      const slide = slides[next];
      if (!slide.classList.contains('product-media-container--image') || slide.hidden) continue;
      const source = slide.querySelector('img');
      if (!source) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'about-gallery-upcoming__button';
      button.setAttribute('aria-label', source.alt || String(next + 1));
      button.dataset.slideIndex = String(next);
      const image = source.cloneNode(false);
      image.removeAttribute('id');
      image.removeAttribute('style');
      image.removeAttribute('class');
      image.removeAttribute('fetchpriority');
      const thumbnailUrl = new URL(source.getAttribute('src'), document.baseURI);
      image.srcset = [96, 128, 160, 216, 324].map((width) => {
        thumbnailUrl.searchParams.set('width', String(width));
        return `${thumbnailUrl.href} ${width}w`;
      }).join(', ');
      thumbnailUrl.searchParams.set('width', '216');
      image.src = thumbnailUrl.href;
      image.sizes = 'auto, 108px';
      image.loading = 'lazy';
      image.alt = '';
      button.append(image);
      button.addEventListener('click', (event) => this.slideshow?.select(next, event));
      buttons.push(button);
    }
    this.#upcoming.replaceChildren(...buttons);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#upcoming?.remove();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
